import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { logger } from '@/lib/logger';
import {
  UserNotification,
  NotificationTemplate,
  Channel,
  UserPreference,
  SendNotificationRequest,
  SendNotificationResponse,
  BulkNotificationRequest,
  BulkNotificationResponse,
  ScheduleNotificationRequest,
  ScheduleNotificationResponse,
  NotificationListParams,
  PaginatedResponse,
  AnalyticsOverview,
  AnalyticsParams,
} from '../types';

const API_BASE_PATH = '/communications';

// Get base URL from axios defaults or environment
const getBaseUrl = () => {
  const apiBaseUrl = import.meta.env.VITE_API_URL;
  if (apiBaseUrl) {
    if (apiBaseUrl.endsWith('/api/v1') || apiBaseUrl.endsWith('/api/v1/')) {
      return apiBaseUrl.replace(/\/$/, '');
    }
    return (apiBaseUrl + '/api/v1').replace(/\/$/, '');
  }
  return '/api/v1';
};

// RTK Query API definition
export const notificationRtkApi = createApi({
  reducerPath: 'notificationApi',
  baseQuery: fetchBaseQuery({
    baseUrl: getBaseUrl(),
    prepareHeaders: async (headers) => {
      try {
        const session = await fetchAuthSession();
        const token = session?.tokens?.accessToken?.toString();
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }
      } catch (e) {
        logger.error('Error getting auth session for notification API:', e);
      }
      return headers;
    },
  }),
  tagTypes: ['Notification', 'Template', 'Channel', 'Preference', 'Analytics'],
  endpoints: (builder) => ({
    // Notification endpoints
    sendNotification: builder.mutation<SendNotificationResponse, SendNotificationRequest>({
      query: (data) => ({
        url: `${API_BASE_PATH}/send`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Notification', 'Analytics'],
    }),

    sendBulkNotifications: builder.mutation<BulkNotificationResponse, BulkNotificationRequest>({
      query: (data) => ({
        url: `${API_BASE_PATH}/send-bulk`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Notification', 'Analytics'],
    }),

    scheduleNotification: builder.mutation<
      ScheduleNotificationResponse,
      ScheduleNotificationRequest
    >({
      query: (data) => ({
        url: `${API_BASE_PATH}/schedule`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Notification'],
    }),

    getNotifications: builder.query<
      PaginatedResponse<UserNotification>,
      NotificationListParams | void
    >({
      query: (params) => ({
        url: `${API_BASE_PATH}/user-notifications`,
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ notification_id }) => ({
                type: 'Notification' as const,
                id: notification_id,
              })),
              { type: 'Notification', id: 'LIST' },
            ]
          : [{ type: 'Notification', id: 'LIST' }],
      // Keep data fresh for 5 minutes
      keepUnusedDataFor: 300,
    }),

    getNotificationById: builder.query<UserNotification, string>({
      query: (id) => `${API_BASE_PATH}/user-notifications/${id}`,
      providesTags: (result, error, id) => [{ type: 'Notification', id }],
    }),

    markAsRead: builder.mutation<UserNotification, string>({
      query: (id) => ({
        url: `${API_BASE_PATH}/user-notifications/${id}/mark_read`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Notification', id },
        { type: 'Notification', id: 'LIST' },
      ],
      // Optimistic update
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          notificationRtkApi.util.updateQueryData('getNotificationById', id, (draft) => {
            draft.is_read = true;
            draft.read_at = new Date().toISOString();
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    // Template endpoints
    getTemplates: builder.query<
      PaginatedResponse<NotificationTemplate>,
      { name?: string; page?: number; page_size?: number } | void
    >({
      query: (params) => ({
        url: `${API_BASE_PATH}/notification-templates`,
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ template_id }) => ({
                type: 'Template' as const,
                id: template_id,
              })),
              { type: 'Template', id: 'LIST' },
            ]
          : [{ type: 'Template', id: 'LIST' }],
    }),

    getTemplateById: builder.query<NotificationTemplate, string>({
      query: (id) => `${API_BASE_PATH}/notification-templates/${id}`,
      providesTags: (result, error, id) => [{ type: 'Template', id }],
    }),

    createTemplate: builder.mutation<
      NotificationTemplate,
      Omit<NotificationTemplate, 'template_id' | 'created_at' | 'updated_at'>
    >({
      query: (template) => ({
        url: `${API_BASE_PATH}/notification-templates`,
        method: 'POST',
        body: template,
      }),
      invalidatesTags: [{ type: 'Template', id: 'LIST' }],
    }),

    updateTemplate: builder.mutation<
      NotificationTemplate,
      { id: string; template: Partial<NotificationTemplate> }
    >({
      query: ({ id, template }) => ({
        url: `${API_BASE_PATH}/notification-templates/${id}`,
        method: 'PUT',
        body: template,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Template', id },
        { type: 'Template', id: 'LIST' },
      ],
    }),

    deleteTemplate: builder.mutation<void, string>({
      query: (id) => ({
        url: `${API_BASE_PATH}/notification-templates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Template', id: 'LIST' }],
    }),

    previewTemplate: builder.mutation<
      { rendered_subject: string; rendered_body: string },
      { id: string; sample_data: Record<string, any> }
    >({
      query: ({ id, sample_data }) => ({
        url: `${API_BASE_PATH}/notification-templates/${id}/preview`,
        method: 'POST',
        body: { sample_data },
      }),
    }),

    // Channel endpoints
    getChannels: builder.query<
      PaginatedResponse<Channel>,
      { is_active?: boolean; page_size?: number } | void
    >({
      query: (params) => ({
        url: `${API_BASE_PATH}/channels`,
        params: params || { is_active: true, page_size: 100 },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ channel_id }) => ({
                type: 'Channel' as const,
                id: channel_id,
              })),
              { type: 'Channel', id: 'LIST' },
            ]
          : [{ type: 'Channel', id: 'LIST' }],
    }),

    // Preference endpoints
    getMyPreferences: builder.query<UserPreference, void>({
      query: () => `${API_BASE_PATH}/user-preferences/my_preferences`,
      providesTags: ['Preference'],
      // Cache for 15 minutes
      keepUnusedDataFor: 900,
    }),

    updateMyPreferences: builder.mutation<UserPreference, Partial<UserPreference>>({
      query: (preferences) => ({
        url: `${API_BASE_PATH}/user-preferences/update_my_preferences`,
        method: 'POST',
        body: preferences,
      }),
      invalidatesTags: ['Preference'],
      async onQueryStarted(preferences, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          notificationRtkApi.util.updateQueryData('getMyPreferences', undefined, (draft) => {
            Object.assign(draft, preferences);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    getUserPreferences: builder.query<UserPreference, string>({
      query: (userId) => `${API_BASE_PATH}/user-preferences/for_user?user=${userId}`,
      providesTags: (result, error, userId) => [{ type: 'Preference', id: userId }],
    }),

    updateUserPreferences: builder.mutation<
      UserPreference,
      { userId: string; preferences: Partial<UserPreference> }
    >({
      query: ({ userId, preferences }) => ({
        url: `${API_BASE_PATH}/user-preferences/for_user?user=${userId}`,
        method: 'PATCH',
        body: preferences,
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: 'Preference', id: userId },
        'Preference',
      ],
    }),

    // Analytics endpoints
    getAnalyticsOverview: builder.query<AnalyticsOverview, AnalyticsParams | void>({
      query: (params) => ({
        url: `${API_BASE_PATH}/analytics`,
        params,
      }),
      providesTags: ['Analytics'],
      // Cache for 1 hour
      keepUnusedDataFor: 3600,
    }),

    getChannelPerformance: builder.query<any, { start_date?: string; end_date?: string } | void>({
      query: (params) => ({
        url: `${API_BASE_PATH}/analytics/channel-performance`,
        params,
      }),
      providesTags: ['Analytics'],
    }),

    getUserEngagement: builder.query<
      any,
      { user_id?: string; start_date?: string; end_date?: string } | void
    >({
      query: (params) => ({
        url: `${API_BASE_PATH}/analytics/user-engagement`,
        params,
      }),
      providesTags: ['Analytics'],
    }),

    exportAnalytics: builder.query<
      Blob | any,
      { format: 'json' | 'csv'; period?: string; start_date?: string; end_date?: string }
    >({
      query: (params) => ({
        url: `${API_BASE_PATH}/analytics/export`,
        params,
        responseHandler: params.format === 'csv' ? 'blob' : 'json',
      }),
    }),

    // Real-time endpoints
    getDueNotifications: builder.query<
      { count: number; results: UserNotification[] },
      { limit?: number } | void
    >({
      query: (params) => ({
        url: `${API_BASE_PATH}/due`,
        params: params || { limit: 100 },
      }),
      providesTags: ['Notification'],
    }),

    dispatchDueNotifications: builder.mutation<
      { processed: number; successes: number; failures: number },
      { limit?: number }
    >({
      query: (data) => ({
        url: `${API_BASE_PATH}/dispatch-due`,
        method: 'POST',
        body: data || { limit: 100 },
      }),
      invalidatesTags: ['Notification', 'Analytics'],
    }),

    // Unread count endpoint
    getUnreadCount: builder.query<number, string | void>({
      query: (userId) => ({
        url: `${API_BASE_PATH}/user-notifications`,
        params: {
          is_read: false,
          page_size: 1,
          ...(userId && { user: userId }),
        },
      }),
      transformResponse: (response: PaginatedResponse<UserNotification>) => response.count,
      providesTags: ['Notification'],
      // Poll every 30 seconds for real-time updates
      pollingInterval: 30000,
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  // Notification hooks
  useSendNotificationMutation,
  useSendBulkNotificationsMutation,
  useScheduleNotificationMutation,
  useGetNotificationsQuery,
  useGetNotificationByIdQuery,
  useMarkAsReadMutation,

  // Template hooks
  useGetTemplatesQuery,
  useGetTemplateByIdQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useDeleteTemplateMutation,
  usePreviewTemplateMutation,

  // Channel hooks
  useGetChannelsQuery,

  // Preference hooks
  useGetMyPreferencesQuery,
  useUpdateMyPreferencesMutation,
  useGetUserPreferencesQuery,
  useUpdateUserPreferencesMutation,

  // Analytics hooks
  useGetAnalyticsOverviewQuery,
  useGetChannelPerformanceQuery,
  useGetUserEngagementQuery,
  useExportAnalyticsQuery,

  // Real-time hooks
  useGetDueNotificationsQuery,
  useDispatchDueNotificationsMutation,
  useGetUnreadCountQuery,
} = notificationRtkApi;
